import { http, HttpResponse, delay } from 'msw';
import { v4 as uuid } from 'uuid';
import { Application as ApplicationDto } from '@electricjs/core_entity-client';

import {
  appDevicesResponse,
  appUserAccountsResponse,
} from '@/mocks/data/applicationAccountsData';
import { applicationsList } from '@/mocks/data/applicationData';
import { createOrganizationApplication } from '@/mocks/data/organizationApplicationData';

const mockIconUrl =
  'https://software-applications-logos.s3.amazonaws.com/supported-applications/1Password.png';

export const applicationHandlers = [
  http.get('*/v1/applications', async () => {
    await delay();
    return HttpResponse.json(
      { total: applicationsList.length, results: applicationsList },
      { status: 200 }
    );
  }),
  http.get('*/v1/applications/:applicationKey', async ({ params }) => {
    const { applicationKey } = params;
    const app = applicationsList.find(
      app => app.id === applicationKey || app.slug === applicationKey
    );

    if (app) {
      await delay(500);
      return HttpResponse.json(app, { status: 200 });
    }

    await delay(500);
    return new HttpResponse(null, { status: 404 });
  }),
  // Powers the Add Applications catalog grid (useListPrivateApplicationsQuery).
  http.get('*/v1/organizations/:organizationId/applications', async () => {
    await delay(200);
    return HttpResponse.json(
      { total: applicationsList.length, results: applicationsList },
      { status: 200 }
    );
  }),
  // Powers the Custom application tab (useCreatePrivateApplicationMutation).
  http.post(
    '*/v1/organizations/:organizationId/applications',
    async ({ request }) => {
      const body = (await request.json()) as { name: string; url?: string };
      const application = {
        id: uuid(),
        name: body.name,
        slug: body.name.trim().toLowerCase().replace(/\s+/g, '-'),
        icon: '',
        ...(body.url ? { url: body.url } : {}),
        integration_availability: 'not_available' as const,
        category: 'other' as const,
      };

      await delay(200);
      return HttpResponse.json(
        {
          application,
          organization_application: {
            ...createOrganizationApplication,
            id: uuid(),
            application,
          },
        },
        { status: 201 }
      );
    }
  ),
  // Admin-side application catalog management (useCreateApplicationMutation).
  http.post('*/v1/applications', async ({ request }) => {
    const body = (await request.json()) as {
      name: string;
      slug: string;
      url?: string;
      user_management_url?: string;
      icon?: string;
      category?: ApplicationDto['category'];
    };
    const application: ApplicationDto = {
      id: uuid(),
      name: body.name,
      slug: body.slug,
      icon: body.icon ?? '',
      url: body.url,
      user_management_url: body.user_management_url,
      integration_availability: 'not_available',
      category: body.category ?? 'other',
    };
    applicationsList.push(application);

    await delay(200);
    return HttpResponse.json(application, { status: 201 });
  }),
  http.post('*/v1/applications/icon', async () => {
    await delay(200);
    return HttpResponse.json({ url: mockIconUrl }, { status: 200 });
  }),
  http.put('*/v1/applications/:applicationKey', async ({ params, request }) => {
    const { applicationKey } = params;
    const body = (await request.json()) as {
      name: string;
      url?: string;
      user_management_url?: string;
      icon?: string;
      integration_availability?: ApplicationDto['integration_availability'];
      category?: ApplicationDto['category'];
    };
    const app = applicationsList.find(
      app => app.id === applicationKey || app.slug === applicationKey
    );

    await delay(200);
    if (!app) {
      return new HttpResponse(null, { status: 404 });
    }
    Object.assign(app, {
      name: body.name,
      url: body.url,
      user_management_url: body.user_management_url,
      ...(body.icon && { icon: body.icon }),
      ...(body.integration_availability && {
        integration_availability: body.integration_availability,
      }),
      ...(body.category && { category: body.category }),
    });
    return HttpResponse.json(app, { status: 200 });
  }),
  http.post(
    '*/v1/organizations/:organizationId/applications/:applicationKey/icon',
    async () => {
      await delay(200);
      return HttpResponse.json({ url: mockIconUrl }, { status: 200 });
    }
  ),
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/users',
    async () => {
      await delay(200);
      return HttpResponse.json(appUserAccountsResponse, { status: 200 });
    }
  ),
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/devices',
    async () => {
      await delay(200);
      return HttpResponse.json(appDevicesResponse, { status: 200 });
    }
  ),
  // Powers the employee "My apps" launcher (useGetMyApplicationsQuery), which
  // requests only applications that have a url.
  http.get(
    '*/v1/organizations/:organizationId/employees/me/applications',
    async () => {
      const results = applicationsList.filter(app => app.url).slice(0, 12);
      await delay(200);
      return HttpResponse.json(
        { total: results.length, results },
        { status: 200 }
      );
    }
  ),
];
